import  { Chain, Quote, userAgent, Response, ChainID, Source } from "./CovalentClient";
import { debugOutput } from "../util/ApiHelpers";
import { parseISO } from "date-fns";
import { ExponentialBackoff } from "../util/backoff";
import pLimit from "p-limit";
import { Chains } from "../util/Chains";
import { ApiKeyValidator } from "../util/ApiKeyValidator";

class TransactionResponse {
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: TransactionResponse) {
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }
}
class Transaction {
    /** * The block signed timestamp in UTC. */
    block_signed_at: Date;
    /** * The height of the block. */
    block_height: number;
    /** * The hash of the block. Use it to remove transactions from re-org-ed blocks. */
    block_hash: string;
    /** * The requested transaction hash. */
    tx_hash: string;
    /** * The offset is the position of the tx in the block. */
    tx_offset: number;
    /** * Indicates whether a transaction failed or succeeded. */
    successful: boolean;
    /** * The sender's wallet address. */
    from_address: string;
    /** * The address of the miner. */
    miner_address: string;
    /** * The label of `from` address. */
    from_address_label: string;
    /** * The receiver's wallet address. */
    to_address: string;
    /** * The label of `to` address. */
    to_address_label: string;
    /** * The value attached to this tx. */
    value: bigint | null;
    /** * The value attached in `quote-currency` to this tx. */
    value_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_value_quote: string;
    /** * The requested chain native gas token metadata. */
    gas_metadata: ContractMetadata;
    gas_offered: number;
    /** * The gas spent for this tx. */
    gas_spent: number;
    /** * The gas price at the time of this tx. */
    gas_price: number;
    /** * The total transaction fees (`gas_price` * `gas_spent`) paid for this tx, denoted in wei. */
    fees_paid: bigint | null;
    /** * The gas spent in `quote-currency` denomination. */
    gas_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_gas_quote: string;
    /** * The native gas exchange rate for the requested `quote-currency`. */
    gas_quote_rate: number;
    /** * The explorer links for this transaction. */
    explorers: Explorer[];
    /** * The details for the dex transaction. */
    dex_details: DexReport[];
    /** * The details for the NFT sale transaction. */
    nft_sale_details: NftSalesReport[];
    /** * The details for the lending protocol transaction. */
    lending_details: LendingReport[];
    /** * The log events. */
    log_events: LogEvent[];
    /** * The details related to the safe transaction. */
    safe_details: SafeDetails[];

    constructor(data: Transaction) {
        this.block_signed_at = data.block_signed_at && data.block_signed_at !== null ? parseISO(data.block_signed_at.toString()) : null;
        this.block_height = data.block_height;
        this.block_hash = data.block_hash;
        this.tx_hash = data.tx_hash;
        this.tx_offset = data.tx_offset;
        this.successful = data.successful;
        this.from_address = data.from_address;
        this.miner_address = data.miner_address;
        this.from_address_label = data.from_address_label;
        this.to_address = data.to_address;
        this.to_address_label = data.to_address_label;
        this.value = data.value && data.value !== null ? BigInt(data.value) : null;
        this.value_quote = data.value_quote;
        this.pretty_value_quote = data.pretty_value_quote;
        this.gas_offered = data.gas_offered;
        this.gas_spent = data.gas_spent;
        this.gas_price = data.gas_price;
        this.fees_paid = data.fees_paid && data.fees_paid !== null ? BigInt(data.fees_paid) : null;
        this.gas_quote = data.gas_quote;
        this.pretty_gas_quote = data.pretty_gas_quote;
        this.gas_quote_rate = data.gas_quote_rate;
        this.gas_metadata = data.gas_metadata && data.gas_metadata !== null ? new ContractMetadata(data.gas_metadata) : null;
        this.explorers = data.explorers && data.explorers !== null ? data.explorers.map((itemData: Explorer) => new Explorer(itemData)) : null;
        this.dex_details = data.dex_details && data.dex_details !== null ? data.dex_details.map((itemData: DexReport) => new DexReport(itemData)) : null;
        this.nft_sale_details = data.nft_sale_details && data.nft_sale_details !== null ? data.nft_sale_details.map((itemData: NftSalesReport) => new NftSalesReport(itemData)) : null;
        this.lending_details = data.lending_details && data.lending_details !== null ? data.lending_details.map((itemData: LendingReport) => new LendingReport(itemData)) : null;
        this.log_events = data.log_events && data.log_events !== null ? data.log_events.map((itemData: LogEvent) => new LogEvent(itemData)) : null;
        this.safe_details = data.safe_details && data.safe_details !== null ? data.safe_details.map((itemData: SafeDetails) => new SafeDetails(itemData)) : null;
    }
}
class ContractMetadata {
    /** * Use contract decimals to format the token balance for display purposes - divide the balance by `10^{contract_decimals}`. */
    contract_decimals: number;
    /** * The string returned by the `name()` method. */
    contract_name: string;
    /** * The ticker symbol for this contract. This field is set by a developer and non-unique across a network. */
    contract_ticker_symbol: string;
    /** * Use the relevant `contract_address` to lookup prices, logos, token transfers, etc. */
    contract_address: string;
    /** * A list of supported standard ERC interfaces, eg: `ERC20` and `ERC721`. */
    supports_erc: string[];
    /** * The contract logo URL. */
    logo_url: string;

    constructor(data: ContractMetadata) {
        this.contract_decimals = data.contract_decimals;
        this.contract_name = data.contract_name;
        this.contract_ticker_symbol = data.contract_ticker_symbol;
        this.contract_address = data.contract_address;
        this.supports_erc = data.supports_erc;
        this.logo_url = data.logo_url;
            
    }
}
class Explorer {
    /** * The name of the explorer. */
    label: string;
    /** * The URL of the explorer. */
    url: string;

    constructor(data: Explorer) {
        this.label = data.label;
        this.url = data.url;
            
    }
}
class DexReport {
    /** * The offset is the position of the log entry within an event log. */
    log_offset: number;
    /** * Stores the name of the protocol that facilitated the event. */
    protocol_name: string;
    /** * Stores the contract address of the protocol that facilitated the event. */
    protocol_address: string;
    /** * The protocol logo URL. */
    protocol_logo_url: string;
    /** * Stores the aggregator responsible for the event. */
    aggregator_name: string;
    /** * Stores the contract address of the aggregator responsible for the event. */
    aggregator_address: string;
    /** * DEXs often have multiple version - e.g Uniswap V1, V2 and V3. The `version` field allows you to look at a specific version of the DEX. */
    version: number;
    /** * Similarly to the `version` field, `fork_version` gives you the version of the forked DEX. For example, most DEXs are a fork of Uniswap V2; therefore, `fork` = `aave` & `fork_version` = `2` */
    fork_version: number;
    /** * Many DEXs are a fork of an already established DEX. The fork field allows you to see which DEX has been forked. */
    fork: string;
    /** * Stores the event taking place - e.g `swap`, `add_liquidity` and `remove_liquidity`. */
    event: string;
    /** * Stores the address of the pair that the user interacts with. */
    pair_address: string;
    pair_lp_fee_bps: number;
    lp_token_address: string;
    lp_token_ticker: string;
    lp_token_num_decimals: number;
    lp_token_name: string;
    lp_token_value: string;
    exchange_rate_usd: number;
    /** * Stores the address of token 0 in the specific pair. */
    token_0_address: string;
    /** * Stores the ticker symbol of token 0 in the specific pair. */
    token_0_ticker: string;
    /** * Stores the number of contract decimals of token 0 in the specific pair. */
    token_0_num_decimals: number;
    /** * Stores the contract name of token 0 in the specific pair. */
    token_0_name: string;
    /** * Stores the address of token 1 in the specific pair. */
    token_1_address: string;
    /** * Stores the ticker symbol of token 1 in the specific pair. */
    token_1_ticker: string;
    /** * Stores the number of contract decimals of token 1 in the specific pair. */
    token_1_num_decimals: number;
    /** * Stores the contract name of token 1 in the specific pair. */
    token_1_name: string;
    /** * Stores the amount of token 0 used in the transaction. For example, 1 ETH, 100 USDC, 30 UNI, etc. */
    token_0_amount: string;
    token_0_quote_rate: number;
    token_0_usd_quote: number;
    pretty_token_0_usd_quote: string;
    token_0_logo_url: string;
    /** * Stores the amount of token 1 used in the transaction. For example, 1 ETH, 100 USDC, 30 UNI, etc. */
    token_1_amount: string;
    token_1_quote_rate: number;
    token_1_usd_quote: number;
    pretty_token_1_usd_quote: string;
    token_1_logo_url: string;
    /** * Stores the wallet address that initiated the transaction (i.e the wallet paying the gas fee). */
    sender: string;
    /** * Stores the recipient of the transaction - recipients can be other wallets or smart contracts. For example, if you want to Swap tokens on Uniswap, the Uniswap router would typically be the recipient of the transaction. */
    recipient: string;

    constructor(data: DexReport) {
        this.log_offset = data.log_offset;
        this.protocol_name = data.protocol_name;
        this.protocol_address = data.protocol_address;
        this.protocol_logo_url = data.protocol_logo_url;
        this.aggregator_name = data.aggregator_name;
        this.aggregator_address = data.aggregator_address;
        this.version = data.version;
        this.fork_version = data.fork_version;
        this.fork = data.fork;
        this.event = data.event;
        this.pair_address = data.pair_address;
        this.pair_lp_fee_bps = data.pair_lp_fee_bps;
        this.lp_token_address = data.lp_token_address;
        this.lp_token_ticker = data.lp_token_ticker;
        this.lp_token_num_decimals = data.lp_token_num_decimals;
        this.lp_token_name = data.lp_token_name;
        this.lp_token_value = data.lp_token_value;
        this.exchange_rate_usd = data.exchange_rate_usd;
        this.token_0_address = data.token_0_address;
        this.token_0_ticker = data.token_0_ticker;
        this.token_0_num_decimals = data.token_0_num_decimals;
        this.token_0_name = data.token_0_name;
        this.token_1_address = data.token_1_address;
        this.token_1_ticker = data.token_1_ticker;
        this.token_1_num_decimals = data.token_1_num_decimals;
        this.token_1_name = data.token_1_name;
        this.token_0_amount = data.token_0_amount;
        this.token_0_quote_rate = data.token_0_quote_rate;
        this.token_0_usd_quote = data.token_0_usd_quote;
        this.pretty_token_0_usd_quote = data.pretty_token_0_usd_quote;
        this.token_0_logo_url = data.token_0_logo_url;
        this.token_1_amount = data.token_1_amount;
        this.token_1_quote_rate = data.token_1_quote_rate;
        this.token_1_usd_quote = data.token_1_usd_quote;
        this.pretty_token_1_usd_quote = data.pretty_token_1_usd_quote;
        this.token_1_logo_url = data.token_1_logo_url;
        this.sender = data.sender;
        this.recipient = data.recipient;
            
    }
}
class NftSalesReport {
    /** * The offset is the position of the log entry within an event log. */
    log_offset: number;
    /** * Stores the topic event hash. All events have a unique topic event hash. */
    topic0: string;
    /** * Stores the contract address of the protocol that facilitated the event. */
    protocol_contract_address: string;
    /** * Stores the name of the protocol that facilitated the event. */
    protocol_name: string;
    /** * The protocol logo URL. */
    protocol_logo_url: string;
    /** * Stores the address of the transaction recipient. */
    to: string;
    /** * Stores the address of the transaction sender. */
    from: string;
    /** * Stores the address selling the NFT. */
    maker: string;
    /** * Stores the address buying the NFT. */
    taker: string;
    /** * Stores the NFTs token ID. All NFTs have a token ID. Within a collection, these token IDs are unique. If the NFT is transferred to another owner, the token id remains the same, as this number is its identifier within a collection. For example, if a collection has 10K NFTs then an NFT in that collection can have a token ID from 1-10K. */
    token_id: string;
    /** * Stores the address of the collection. For example, [Bored Ape Yacht Club](https://etherscan.io/token/0xbc4ca0eda7647a8ab7c2061c2e118a18a936f13d) */
    collection_address: string;
    /** * Stores the name of the collection. */
    collection_name: string;
    /** * Stores the address of the token used to purchase the NFT. */
    token_address: string;
    /** * Stores the name of the token used to purchase the NFT. */
    token_name: string;
    /** * Stores the ticker symbol of the token used to purchase the NFT. */
    ticker_symbol: string;
    /** * Stores the number decimal of the token used to purchase the NFT. */
    num_decimals: number;
    contract_quote_rate: number;
    /** * The token amount used to purchase the NFT. For example, if the user purchased an NFT for 1 ETH. The `nft_token_price` field will hold `1`. */
    nft_token_price: number;
    /** * The USD amount used to purchase the NFT. */
    nft_token_price_usd: number;
    pretty_nft_token_price_usd: string;
    /** * The price of the NFT denominated in the chains native token. Even if a seller sells their NFT for DAI or MANA, this field denominates the price in the native token (e.g. ETH, AVAX, FTM, etc.) */
    nft_token_price_native: number;
    pretty_nft_token_price_native: string;
    /** * Stores the number of NFTs involved in the sale. It's quick routine to see multiple NFTs involved in a single sale. */
    token_count: number;
    num_token_ids_sold_per_sale: number;
    num_token_ids_sold_per_tx: number;
    num_collections_sold_per_sale: number;
    num_collections_sold_per_tx: number;
    trade_type: string;
    trade_group_type: string;

    constructor(data: NftSalesReport) {
        this.log_offset = data.log_offset;
        this.topic0 = data.topic0;
        this.protocol_contract_address = data.protocol_contract_address;
        this.protocol_name = data.protocol_name;
        this.protocol_logo_url = data.protocol_logo_url;
        this.to = data.to;
        this.from = data.from;
        this.maker = data.maker;
        this.taker = data.taker;
        this.token_id = data.token_id;
        this.collection_address = data.collection_address;
        this.collection_name = data.collection_name;
        this.token_address = data.token_address;
        this.token_name = data.token_name;
        this.ticker_symbol = data.ticker_symbol;
        this.num_decimals = data.num_decimals;
        this.contract_quote_rate = data.contract_quote_rate;
        this.nft_token_price = data.nft_token_price;
        this.nft_token_price_usd = data.nft_token_price_usd;
        this.pretty_nft_token_price_usd = data.pretty_nft_token_price_usd;
        this.nft_token_price_native = data.nft_token_price_native;
        this.pretty_nft_token_price_native = data.pretty_nft_token_price_native;
        this.token_count = data.token_count;
        this.num_token_ids_sold_per_sale = data.num_token_ids_sold_per_sale;
        this.num_token_ids_sold_per_tx = data.num_token_ids_sold_per_tx;
        this.num_collections_sold_per_sale = data.num_collections_sold_per_sale;
        this.num_collections_sold_per_tx = data.num_collections_sold_per_tx;
        this.trade_type = data.trade_type;
        this.trade_group_type = data.trade_group_type;
            
    }
}
class LendingReport {
    /** * The offset is the position of the log entry within an event log. */
    log_offset: number;
    /** * Stores the name of the lending protocol that facilitated the event. */
    protocol_name: string;
    /** * Stores the contract address of the lending protocol that facilitated the event. */
    protocol_address: string;
    /** * The protocol logo URL. */
    protocol_logo_url: string;
    /** * Lending protocols often have multiple version (e.g. Aave V1, V2 and V3). The `version` field allows you to look at a specific version of the Lending protocol. */
    version: string;
    /** * Many lending protocols are a fork of an already established protocol. The fork column allows you to see which lending protocol has been forked. */
    fork: string;
    /** * Similarly to the `version` column, `fork_version` gives you the version of the forked lending protocol. For example, most lending protocols in the space are a fork of Aave V2; therefore, `fork` = `aave` & `fork_version` = `2` */
    fork_version: string;
    /** * Stores the event taking place - e.g `borrow`, `deposit`, `liquidation`, 'repay' and 'withdraw'. */
    event: string;
    /** * Stores the name of the LP token issued by the lending protocol. LP tokens can be debt or interest bearing tokens. */
    lp_token_name: string;
    /** * Stores the number decimal of the LP token. */
    lp_decimals: number;
    /** * Stores the ticker symbol of the LP token. */
    lp_ticker_symbol: string;
    /** * Stores the token address of the LP token. */
    lp_token_address: string;
    /** * Stores the amount of LP token used in the event (e.g. 1 aETH, 100 cUSDC, etc). */
    lp_token_amount: number;
    /** * Stores the total USD amount of all the LP Token used in the event. */
    lp_token_price: number;
    /** * Stores the exchange rate between the LP and underlying token. */
    exchange_rate: number;
    /** * Stores the USD price of the LP Token used in the event. */
    exchange_rate_usd: number;
    /** * Stores the name of the token going into the lending protocol (e.g the token getting deposited). */
    token_name_in: string;
    /** * Stores the number decimal of the token going into the lending protocol. */
    token_decimal_in: number;
    /** * Stores the address of the token going into the lending protocol. */
    token_address_in: string;
    /** * Stores the ticker symbol of the token going into the lending protocol. */
    token_ticker_in: string;
    /** * Stores the logo URL of the token going into the lending protocol. */
    token_logo_in: string;
    /** * Stores the amount of tokens going into the lending protocol (e.g 1 ETH, 100 USDC, etc). */
    token_amount_in: number;
    /** * Stores the total USD amount of all tokens going into the lending protocol. */
    amount_in_usd: number;
    pretty_amount_in_usd: string;
    /** * Stores the name of the token going out of the lending protocol (e.g the token getting deposited). */
    token_name_out: string;
    /** * Stores the number decimal of the token going out of the lending protocol. */
    token_decimals_out: number;
    /** * Stores the address of the token going out of the lending protocol. */
    token_address_out: string;
    /** * Stores the ticker symbol of the token going out of the lending protocol. */
    token_ticker_out: string;
    /** * Stores the logo URL of the token going out of the lending protocol. */
    token_logo_out: string;
    /** * Stores the amount of tokens going out of the lending protocol (e.g 1 ETH, 100 USDC, etc). */
    token_amount_out: number;
    /** * Stores the total USD amount of all tokens going out of the lending protocol. */
    amount_out_usd: number;
    pretty_amount_out_usd: string;
    /** * Stores the type of loan the user is taking out. Lending protocols enable you to take out a stable or variable loan. Only relevant to borrow events. */
    borrow_rate_mode: number;
    /** * Stores the interest rate of the loan. Only relevant to borrow events. */
    borrow_rate: number;
    on_behalf_of: string;
    /** * Stores the wallet address liquidating the loan. Only relevant to liquidation events. */
    liquidator: string;
    /** * Stores the wallet address of the user initiating the event. */
    user: string;

    constructor(data: LendingReport) {
        this.log_offset = data.log_offset;
        this.protocol_name = data.protocol_name;
        this.protocol_address = data.protocol_address;
        this.protocol_logo_url = data.protocol_logo_url;
        this.version = data.version;
        this.fork = data.fork;
        this.fork_version = data.fork_version;
        this.event = data.event;
        this.lp_token_name = data.lp_token_name;
        this.lp_decimals = data.lp_decimals;
        this.lp_ticker_symbol = data.lp_ticker_symbol;
        this.lp_token_address = data.lp_token_address;
        this.lp_token_amount = data.lp_token_amount;
        this.lp_token_price = data.lp_token_price;
        this.exchange_rate = data.exchange_rate;
        this.exchange_rate_usd = data.exchange_rate_usd;
        this.token_name_in = data.token_name_in;
        this.token_decimal_in = data.token_decimal_in;
        this.token_address_in = data.token_address_in;
        this.token_ticker_in = data.token_ticker_in;
        this.token_logo_in = data.token_logo_in;
        this.token_amount_in = data.token_amount_in;
        this.amount_in_usd = data.amount_in_usd;
        this.pretty_amount_in_usd = data.pretty_amount_in_usd;
        this.token_name_out = data.token_name_out;
        this.token_decimals_out = data.token_decimals_out;
        this.token_address_out = data.token_address_out;
        this.token_ticker_out = data.token_ticker_out;
        this.token_logo_out = data.token_logo_out;
        this.token_amount_out = data.token_amount_out;
        this.amount_out_usd = data.amount_out_usd;
        this.pretty_amount_out_usd = data.pretty_amount_out_usd;
        this.borrow_rate_mode = data.borrow_rate_mode;
        this.borrow_rate = data.borrow_rate;
        this.on_behalf_of = data.on_behalf_of;
        this.liquidator = data.liquidator;
        this.user = data.user;
            
    }
}
class LogEvent {
    /** * The block signed timestamp in UTC. */
    block_signed_at: Date;
    /** * The height of the block. */
    block_height: number;
    /** * The offset is the position of the tx in the block. */
    tx_offset: number;
    /** * The offset is the position of the log entry within an event log. */
    log_offset: number;
    /** * The requested transaction hash. */
    tx_hash: string;
    /** * The log topics in raw data. */
    raw_log_topics: string[];
    /** * Use contract decimals to format the token balance for display purposes - divide the balance by `10^{contract_decimals}`. */
    sender_contract_decimals: number;
    /** * The name of the sender. */
    sender_name: string;
    sender_contract_ticker_symbol: string;
    /** * The address of the sender. */
    sender_address: string;
    /** * The label of the sender address. */
    sender_address_label: string;
    /** * The contract logo URL. */
    sender_logo_url: string;
    /** * A list of supported standard ERC interfaces, eg: `ERC20` and `ERC721`. */
    supports_erc: string[];
    /** * The address of the deployed UniswapV2 like factory contract for this DEX. */
    sender_factory_address: string;
    /** * The log events in raw. */
    raw_log_data: string;
    /** * The decoded item. */
    decoded: DecodedItem;

    constructor(data: LogEvent) {
        this.block_signed_at = data.block_signed_at && data.block_signed_at !== null ? parseISO(data.block_signed_at.toString()) : null;
        this.block_height = data.block_height;
        this.tx_offset = data.tx_offset;
        this.log_offset = data.log_offset;
        this.tx_hash = data.tx_hash;
        this.raw_log_topics = data.raw_log_topics;
        this.sender_contract_decimals = data.sender_contract_decimals;
        this.sender_name = data.sender_name;
        this.sender_contract_ticker_symbol = data.sender_contract_ticker_symbol;
        this.sender_address = data.sender_address;
        this.sender_address_label = data.sender_address_label;
        this.sender_logo_url = data.sender_logo_url;
        this.supports_erc = data.supports_erc;
        this.sender_factory_address = data.sender_factory_address;
        this.raw_log_data = data.raw_log_data;
        this.decoded = data.decoded && data.decoded !== null ? new DecodedItem(data.decoded) : null;
    }
}
class SafeDetails {
    /** * The address that signed the safe transaction. */
    owner_address: string;
    /** * The signature of the owner for the safe transaction. */
    signature: string;
    /** * The type of safe signature used. */
    signature_type: string;

    constructor(data: SafeDetails) {
        this.owner_address = data.owner_address;
        this.signature = data.signature;
        this.signature_type = data.signature_type;
            
    }
}
class DecodedItem {
    name: string;
    signature: string;
    params: Param[];

    constructor(data: DecodedItem) {
        this.name = data.name;
        this.signature = data.signature;
        this.params = data.params && data.params !== null ? data.params.map((itemData: Param) => new Param(itemData)) : null;
    }
}
class Param {
    name: string;
    type: string;
    indexed: boolean;
    decoded: boolean;
    value: string;

    constructor(data: Param) {
        this.name = data.name;
        this.type = data.type;
        this.indexed = data.indexed;
        this.decoded = data.decoded;
        this.value = data.value;
            
    }
}
class RecentTransactionsResponse {
    /** * The requested address. */
    address: string;
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested quote currency eg: `USD`. */
    quote_currency: string;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * The current page of the response. */
    current_page: number;
    links: PaginationLinks;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: RecentTransactionsResponse, private _debug: boolean, private _apiKey: string, private _threadCount: number, private _urlParams: URLSearchParams, private _enableRetry: boolean, private _source: Source) {
        this.address = data.address;
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.quote_currency = data.quote_currency;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.current_page = data.current_page;
        this.links = data.links && data.links !== null ? new PaginationLinks(data.links) : null;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }

    async prev(): Promise<Response<RecentTransactionsResponse>> {
        let success = false;
        let data: Response<RecentTransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<RecentTransactionsResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.prev === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.prev}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.prev}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new RecentTransactionsResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    async next(): Promise<Response<RecentTransactionsResponse>> {
        let success = false;
        let data: Response<RecentTransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<RecentTransactionsResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.next === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.next}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.next}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new RecentTransactionsResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
}
class PaginationLinks {
    /** * URL link to the next page. */
    prev: string;
    /** * URL link to the previous page. */
    next: string;

    constructor(data: PaginationLinks) {
        this.prev = data.prev;
        this.next = data.next;  
    }
}
class TransactionsBlockPageResponse {
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    links: PaginationLinks;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: TransactionsBlockPageResponse, private _debug: boolean, private _apiKey: string, private _threadCount: number, private _urlParams: URLSearchParams, private _enableRetry: boolean, private _source: Source) {
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.links = data.links && data.links !== null ? new PaginationLinks(data.links) : null;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }

    async prev(): Promise<Response<TransactionsBlockPageResponse>> {
        let success = false;
        let data: Response<TransactionsBlockPageResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsBlockPageResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.prev === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.prev}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.prev}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsBlockPageResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    async next(): Promise<Response<TransactionsBlockPageResponse>> {
        let success = false;
        let data: Response<TransactionsBlockPageResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsBlockPageResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.next === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.next}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.next}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsBlockPageResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }


}
class TransactionsBlockResponse {
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: TransactionsBlockResponse) {
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }
}
class TransactionsSummaryResponse {
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested address. */
    address: string;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * List of response items. */
    items: TransactionsSummary[];

    constructor(data: TransactionsSummaryResponse) {
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.address = data.address;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.items = data.items && data.items !== null ? data.items.map((itemData: TransactionsSummary) => new TransactionsSummary(itemData)) : null;
    }
}
class GasSummary {
    /** * The total number of transactions sent by the address. */
    total_sent_count: number;
    /** * The total transaction fees paid by the address, denoted in wei. */
    total_fees_paid: bigint | null;
    /** * The total transaction fees paid by the address, denoted in `quote-currency`. */
    total_gas_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_total_gas_quote: string;
    /** * The average gas quote per transaction. */
    average_gas_quote_per_tx: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_average_gas_quote_per_tx: string;
    /** * The requested chain native gas token metadata. */
    gas_metadata: ContractMetadata;

    constructor(data: GasSummary) {
        this.total_sent_count = data.total_sent_count;
        this.total_fees_paid = data.total_fees_paid && data.total_fees_paid !== null ? BigInt(data.total_fees_paid) : null;
        this.total_gas_quote = data.total_gas_quote;
        this.pretty_total_gas_quote = data.pretty_total_gas_quote;
        this.average_gas_quote_per_tx = data.average_gas_quote_per_tx;
        this.pretty_average_gas_quote_per_tx = data.pretty_average_gas_quote_per_tx;
        this.gas_metadata = data.gas_metadata && data.gas_metadata !== null ? new ContractMetadata(data.gas_metadata) : null;
    }
}
class TransactionsSummary {
    /** * The total number of transactions. */
    total_count: number;
    /** * The earliest transaction detected. */
    earliest_transaction: TransactionSummary;
    /** * The latest transaction detected. */
    latest_transaction: TransactionSummary;
    /** * The gas summary for the transactions. */
    gas_summary: GasSummary;

    constructor(data: TransactionsSummary) {
        this.total_count = data.total_count;
        this.earliest_transaction = data.earliest_transaction && data.earliest_transaction !== null ? new TransactionSummary(data.earliest_transaction) : null;
        this.latest_transaction = data.latest_transaction && data.latest_transaction !== null ? new TransactionSummary(data.latest_transaction) : null;
        this.gas_summary = data.gas_summary && data.gas_summary !== null ? new GasSummary(data.gas_summary) : null;
    }
}
class TransactionSummary {
    /** * The block signed timestamp in UTC. */
    block_signed_at: Date;
    /** * The requested transaction hash. */
    tx_hash: string;
    /** * The link to the transaction details using the Covalent API. */
    tx_detail_link: string;

    constructor(data: TransactionSummary) {
        this.block_signed_at = data.block_signed_at && data.block_signed_at !== null ? parseISO(data.block_signed_at.toString()) : null;
        this.tx_hash = data.tx_hash;
        this.tx_detail_link = data.tx_detail_link;
            
    }
}
class TransactionsResponse {
    /** * The requested address. */
    address: string;
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested quote currency eg: `USD`. */
    quote_currency: string;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * The current page of the response. */
    current_page: number;
    links: PaginationLinks;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: TransactionsResponse, private _debug: boolean, private _apiKey: string, private _threadCount: number, private _urlParams: URLSearchParams, private _enableRetry: boolean, private _source: Source) {
        this.address = data.address;
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.quote_currency = data.quote_currency;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.current_page = data.current_page;
        this.links = data.links && data.links !== null ? new PaginationLinks(data.links) : null;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }

    async prev(): Promise<Response<TransactionsResponse>> {
        let success = false;
        let data: Response<TransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.prev === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.prev}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.prev}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    async next(): Promise<Response<TransactionsResponse>> {
        let success = false;
        let data: Response<TransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.next === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.next}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.next}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

}
class TransactionsTimeBucketResponse {
    /** * The requested address. */
    address: string;
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested quote currency eg: `USD`. */
    quote_currency: string;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    complete: boolean;
    /** * The current bucket of the response. */
    current_bucket: number;
    links: PaginationLinks;
    /** * List of response items. */
    items: Transaction[];

    constructor(data: TransactionsTimeBucketResponse, private _debug: boolean, private _apiKey: string, private _threadCount: number, private _urlParams: URLSearchParams, private _enableRetry: boolean, private _source: Source) {
        this.address = data.address;
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.quote_currency = data.quote_currency;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.complete = data.complete;
        this.current_bucket = data.current_bucket;
        this.links = data.links && data.links !== null ? new PaginationLinks(data.links) : null;
        this.items = data.items && data.items !== null ? data.items.map((itemData: Transaction) => new Transaction(itemData)) : null;
    }

    async prev(): Promise<Response<TransactionsTimeBucketResponse>> {
        let success = false;
        let data: Response<TransactionsTimeBucketResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsTimeBucketResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.prev === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.prev}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.prev}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsTimeBucketResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    async next(): Promise<Response<TransactionsTimeBucketResponse>> {
        let success = false;
        let data: Response<TransactionsTimeBucketResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsTimeBucketResponse>>(this._apiKey, this._debug);
        const LIMIT = pLimit(this._threadCount);
        while (!success) {
            try {

                let startTime;
                if (this._debug) {
                    startTime = new Date();
                }

                if (this.links.next === null) {
                    success = true;
                    return {
                        data: null,
                        error: true,
                        error_code: 400,
                        error_message: "Invalid URL: URL link cannot be null"
                    }
                }

                const url = `${this.links.next}?${this._urlParams}`;

                response = await LIMIT(() => fetch(`${this.links.next}?${this._urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this._apiKey}`,
                        "X-Requested-With": this._source ? this._source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this._enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await LIMIT(() => backoff.backOff(response ? response.url : url, this._source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsTimeBucketResponse(data.data, this._debug, this._apiKey, this._threadCount, this._urlParams, this._enableRetry, this._source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

}

export interface GetTransactionQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Decoded DEX details including protocol (e.g. Uniswap), event (e.g 'add_liquidity') and tokens involved with historical prices. Additional 0.05 credits charged if data available. */
    withDex?: boolean;
    /** * Decoded NFT sales details including marketplace (e.g. Opensea) and cached media links. Additional 0.05 credits charged if data available. */
    withNftSales?: boolean;
    /** * Decoded lending details including protocol (e.g. Aave), event (e.g. 'deposit') and tokens involved with prices. Additional 0.05 credits charged if data available. */
    withLending?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetAllTransactionsForAddressQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order. */
    blockSignedAtAsc?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTransactionsForBlockQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTransactionsForAddressV3QueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order. */
    blockSignedAtAsc?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTimeBucketTransactionsForAddressQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTransactionsForBlockHashByPageQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTransactionsForBlockHashQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Omit log events. */
    noLogs?: boolean;
    /** * Include safe details. */
    withSafe?: boolean;
}
export interface GetTransactionSummaryQueryParamOpts {
    /** * The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`. */
    quoteCurrency?: Quote;
    /** * Include gas summary details. Additional charge of 1 credit when true. Response times may be impacted for wallets with millions of transactions. */
    withGas?: boolean;
}

/**
 * Transactions APIs
 * 
 */

async function* paginateEndpoint(url: string, apiKey: string, urlsParams: URLSearchParams, debug: boolean, threadCount: number, enableRetry: boolean, source: Source): AsyncIterable<Transaction> {
    let hasNext = true;
    let response: globalThis.Response;
    let data: Response<RecentTransactionsResponse>;
    const backoff = new ExponentialBackoff<Response<RecentTransactionsResponse>>(apiKey, debug);
    const LIMIT = pLimit(threadCount);
    while (hasNext) {
        try {

            let startTime;
            if (debug) {
                startTime = new Date();
            }

            response = await LIMIT(() => fetch(`${url}?${urlsParams}`, {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "X-Requested-With": source ? source + " " + "(" + userAgent + ")" : userAgent
                }
            }));

            debugOutput(response ? response.url : url, response ? response.status : 429, startTime);
        
            if ((enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                try {
                    data = await LIMIT(() => backoff.backOff(response ? response.url : `${url}?${urlsParams}`, this.source));
                } catch (error) {
                    hasNext = false;
                    throw new Error(`An error occurred ${response ? response.status : 429}: ${error.message}`);
                }
            } else {
                data = await response.json();
            }
            
            for (const tx of data.data.items) {
                const dataClass = new Transaction(tx);
                
                yield dataClass;
            }
            backoff.setNumAttempts(1);
            if (!data.error) {
                if ((data.data !== null) && data.data.links.prev === null) {
                    hasNext = false;
                }
                url = data.data !== null ? data.data.links.prev : "";
            } else {
                hasNext = false;
            }

        } catch (error) {
            hasNext = false;
            if (error.message.includes("An error occurred 429")) {
                throw new Error(error.message);
            }
            throw new Error(`An error occurred ${data ? data.error_code : response ? response.status : "Unknown Error Code"}: ${data ? data.error_message : response && response.status === 500 ? "Internal server error" : "Unknown Error"}`);
        }
    }
    
}
            
export class TransactionService {
    private LIMIT;
    constructor(private apiKey: string, private debug: boolean = false, private threadCount: number = 3, private is_key_valid: boolean, private enableRetry: boolean = true, private source: Source) {
        this.LIMIT = pLimit(this.threadCount);
    }


    /**
     *
     * Commonly used to fetch and render a single transaction including its decoded log events. Additionally return semantically decoded information for DEX trades, lending and NFT sales.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} txHash - The transaction hash.
     * @param {GetTransactionQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `withDex`: Decoded DEX details including protocol (e.g. Uniswap), event (e.g 'add_liquidity') and tokens involved with historical prices. Additional 0.05 credits charged if data available.
     *   - `withNftSales`: Decoded NFT sales details including marketplace (e.g. Opensea) and cached media links. Additional 0.05 credits charged if data available.
     *   - `withLending`: Decoded lending details including protocol (e.g. Aave), event (e.g. 'deposit') and tokens involved with prices. Additional 0.05 credits charged if data available.
     *   - `withSafe`: Include safe details.
     * 
     */
    public async getTransaction(chainName: Chain | Chains | ChainID, txHash: string, queryParamOpts?: GetTransactionQueryParamOpts): Promise<Response<TransactionResponse>> {
        let success = false;
        let data: Response<TransactionResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.withDex !== undefined) {
                    urlParams.append("with-dex", queryParamOpts?.withDex.toString());
                }
                    
                if (queryParamOpts?.withNftSales !== undefined) {
                    urlParams.append("with-nft-sales", queryParamOpts?.withNftSales.toString());
                }
                    
                if (queryParamOpts?.withLending !== undefined) {
                    urlParams.append("with-lending", queryParamOpts?.withLending.toString());
                }

                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                const url = `https://api.covalenthq.com/v1/${chainName}/transaction_v2/${txHash}/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/transaction_v2/${txHash}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
        
    /**
     *
     * Commonly used to fetch and render the most recent transactions involving an address. Frequently seen in wallet applications.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetAllTransactionsForAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
     *   - `withSafe`: Include safe details.
     * 
     */
    public async* getAllTransactionsForAddress(chainName: Chain | Chains | ChainID, walletAddress: string, queryParamOpts?: GetAllTransactionsForAddressQueryParamOpts): AsyncIterable<Transaction> {
        let success = false;
        let tx: Transaction;
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    throw new Error(`An error occurred 401: ${ApiKeyValidator.INVALID_API_KEY_MESSAGE}`);
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.blockSignedAtAsc !== undefined) {
                    urlParams.append("block-signed-at-asc", queryParamOpts?.blockSignedAtAsc.toString());
                }

                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                for await (tx of paginateEndpoint(`https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_v3/`, this.apiKey, urlParams, this.debug, this.threadCount, this.enableRetry, this.source)) {
                    yield tx;
                }

                success = true;
            } catch (error) {
                success = true;
                throw new Error(error.message);
            }
        }
    }

    /**
     *
     * Commonly used to fetch and render the most recent transactions involving an address. Frequently seen in wallet applications.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetAllTransactionsForAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
     *   - `withSafe`: Include safe details.
     * 
     */
    public async getAllTransactionsForAddressByPage(chainName: Chain | Chains | ChainID, walletAddress: string, queryParamOpts?: GetAllTransactionsForAddressQueryParamOpts): Promise<Response<RecentTransactionsResponse>> {
        let success = false;
        let data: Response<RecentTransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<RecentTransactionsResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.blockSignedAtAsc !== undefined) {
                    urlParams.append("block-signed-at-asc", queryParamOpts?.blockSignedAtAsc.toString());
                }

                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                const url = `https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_v3/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_v3/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new RecentTransactionsResponse(data.data, this.debug, this.apiKey, this.threadCount, urlParams, this.enableRetry, this.source);
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
     
    /**
     *
     * Commonly used to fetch all transactions including their decoded log events in a block and further flag interesting wallets or transactions.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {number} blockHeight - The requested block height.
     * @param {GetTransactionsForBlockQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `withSafe`: Include safe details.
     *
     */
    public async getTransactionsForBlock(chainName: Chain | Chains | ChainID, blockHeight: number | string, queryParamOpts?: GetTransactionsForBlockQueryParamOpts): Promise<Response<TransactionsBlockResponse>> {
        let success = false;
        let data: Response<TransactionsBlockResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsBlockResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }

                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                const url = `https://api.covalenthq.com/v1/${chainName}/block/${blockHeight}/transactions_v3/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/block/${blockHeight}/transactions_v3/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsBlockResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
        
    /**
     *
     * Commonly used to fetch the earliest and latest transactions, and the transaction count for a wallet. Calculate the age of the wallet and the time it has been idle and quickly gain insights into their engagement with web3.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetTransactionSummaryQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `withGas`: Include gas summary details. Additional charge of 1 credit when true. Response times may be impacted for wallets with millions of transactions.
     *
     */
    public async getTransactionSummary(chainName: Chain | Chains | ChainID, walletAddress: string, queryParamOpts?: GetTransactionSummaryQueryParamOpts): Promise<Response<TransactionsSummaryResponse>> {
        let success = false;
        let data: Response<TransactionsSummaryResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsSummaryResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }

                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.withGas !== undefined) {
                    urlParams.append("with-gas", queryParamOpts?.withGas.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                const url = `https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_summary/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_summary/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsSummaryResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    /**
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {number} page - The requested page, 0-indexed.
     * @param {GetTransactionsForAddressV3QueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `blockSignedAtAsc`: Sort the transactions in ascending chronological order. By default, it's set to `false` and returns transactions in descending chronological order.
     *   - `withSafe`: Include safe details.
     *
     */
    public async getTransactionsForAddressV3(chainName: Chain | Chains | ChainID, walletAddress: string, page: number, queryParamOpts?: GetTransactionsForAddressV3QueryParamOpts): Promise<Response<TransactionsResponse>> {
        let success = false;
        let data: Response<TransactionsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.blockSignedAtAsc !== undefined) {
                    urlParams.append("block-signed-at-asc", queryParamOpts?.blockSignedAtAsc.toString());
                }
                    
                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                const url = `https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_v3/page/${page}/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/address/${walletAddress}/transactions_v3/page/${page}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                
                const dataClass = new TransactionsResponse(data.data, this.debug, this.apiKey, this.threadCount, urlParams, this.enableRetry, this.source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };

            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    /**
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {number} timeBucket - The 0-indexed 15-minute time bucket. E.g. 27 Feb 2023 05:23 GMT = 1677475383 (Unix time). 1677475383/900=1863861 timeBucket.
     * @param {GetTimeBucketTransactionsForAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `withSafe`: Include safe details.
     *
     */
    public async getTimeBucketTransactionsForAddress(chainName: Chain | Chains | ChainID, walletAddress: string, timeBucket: number, queryParamOpts?: GetTimeBucketTransactionsForAddressQueryParamOpts): Promise<Response<TransactionsTimeBucketResponse>> {
        let success = false;
        let data: Response<TransactionsTimeBucketResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsTimeBucketResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }
                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }  
                
                const url = `https://api.covalenthq.com/v1/${chainName}/bulk/transactions/${walletAddress}/${timeBucket}/?${urlParams}`;

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/bulk/transactions/${walletAddress}/${timeBucket}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                
                const dataClass = new TransactionsTimeBucketResponse(data.data, this.debug, this.apiKey, this.threadCount, urlParams, this.enableRetry, this.source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    /**
     *
     * Commonly used to fetch all transactions including their decoded log events in a block and further flag interesting wallets or transactions.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} blockHash - The requested block hash.
     * @param {number} page - The requested 0-indexed page number.
     * @param {GetTransactionsForBlockHashByPageQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `withSafe`: Include safe details.
     *
     */
    public async getTransactionsForBlockHashByPage(chainName: Chain | Chains | ChainID, blockHash: string, page: number, queryParamOpts?: GetTransactionsForBlockHashByPageQueryParamOpts): Promise<Response<TransactionsBlockPageResponse>> {
        let success = false;
        let data: Response<TransactionsBlockPageResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsBlockPageResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }

                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }
                    

                const url = `https://api.covalenthq.com/v1/${chainName}/block_hash/${blockHash}/transactions_v3/page/${page}/?${urlParams}`;

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/block_hash/${blockHash}/transactions_v3/page/${page}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsBlockPageResponse(data.data, this.debug, this.apiKey, this.threadCount, urlParams, this.enableRetry, this.source);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
        
    /**
     *
     * Commonly used to fetch all transactions including their decoded log events in a block and further flag interesting wallets or transactions.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} blockHash - The requested block hash.
     * @param {GetTransactionsForBlockHashQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noLogs`: Omit log events.
     *   - `withSafe`: Include safe details.
     *
     */
    public async getTransactionsForBlockHash(chainName: Chain | Chains | ChainID, blockHash: string, queryParamOpts?: GetTransactionsForBlockHashQueryParamOpts): Promise<Response<TransactionsBlockResponse>> {
        let success = false;
        let data: Response<TransactionsBlockResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<TransactionsBlockResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }

                
                if (queryParamOpts?.quoteCurrency !== undefined) {
                    urlParams.append("quote-currency", queryParamOpts?.quoteCurrency.toString());
                }
                    
                if (queryParamOpts?.noLogs !== undefined) {
                    urlParams.append("no-logs", queryParamOpts?.noLogs.toString());
                }
                    
                if (queryParamOpts?.withSafe !== undefined) {
                    urlParams.append("with-safe", queryParamOpts?.withSafe.toString());
                }
                    

                const url = `https://api.covalenthq.com/v1/${chainName}/block_hash/${blockHash}/transactions_v3/?${urlParams}`;

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/block_hash/${blockHash}/transactions_v3/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new TransactionsBlockResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }
        

}
    
    